import { Injectable, NgZone } from "@angular/core";
import { Observable, ReplaySubject, Subject } from "rxjs";
import { SocialUser } from "@abacritt/angularx-social-login";
import { GOOGLE_LOGIN_CLIENT_ID } from "../common/global-constants";

declare var google: any;

/** the subset of google.accounts.id.renderButton options we use */
export interface IGoogleButtonOptions {
    type?: "standard" | "icon";
    size?: "large" | "medium" | "small";
    text?: "signin_with" | "signup_with" | "continue_with" | "signin";
    theme?: "outline" | "filled_blue" | "filled_black";
    shape?: "rectangular" | "pill" | "circle" | "square";
    width?: number;
    logo_alignment?: "left" | "center";
}

/**
 * Google Identity Services (GIS) wrapper for the Google login on the login page.
 *
 * Why not the GoogleLoginProvider of @abacritt/angularx-social-login: that provider is constructed at app boot,
 * loads the GIS script right away and, with its default oneTapEnabled, calls google.accounts.id.prompt() whenever
 * no social user is set. The One Tap / FedCM chooser therefore popped up app wide, also for users who logged in
 * with a password, and there was no way to open the chooser on demand from the Google icon.
 *
 * This service loads nothing until the login page asks for it, initializes GIS once with FedCM enabled, renders
 * the official button on request and exposes prompt() for a one click sign in. The credential it emits is the
 * same Google ID token mars verifies at GET /googleAuth/:token (audience GOOGLE_LOGIN_CLIENT_ID), so the server
 * contract did not change and older studio builds keep working against the same endpoint.
 */
@Injectable({ providedIn: "root" })
export class GoogleIdentityService {

    private static readonly SCRIPT_ID = "google-gsi-client";
    private static readonly SCRIPT_SRC = "https://accounts.google.com/gsi/client";

    private m_ready$ = new ReplaySubject<boolean>(1);
    private m_credential$ = new Subject<SocialUser>();
    private m_loadStarted = false;
    private m_initialized = false;

    constructor(private ngZone: NgZone) {
    }

    /** emits a SocialUser (provider GOOGLE, idToken = the raw credential) each time Google returns a credential */
    get credential$(): Observable<SocialUser> {
        return this.m_credential$.asObservable();
    }

    /**
     * lazily loads the GIS script and initializes it once
     * emits true when ready, false when the script cannot load (offline, blocked); a later call retries
     */
    load(): Observable<boolean> {
        if (!this.m_loadStarted) {
            this.m_loadStarted = true;
            this.m_ready$ = new ReplaySubject<boolean>(1);
            if (this.isAvailable()) {
                this.initialize();
            } else {
                const script = document.createElement("script");
                script.id = GoogleIdentityService.SCRIPT_ID;
                script.src = GoogleIdentityService.SCRIPT_SRC;
                script.async = true;
                script.defer = true;
                script.onload = () => this.ngZone.run(() => this.initialize());
                script.onerror = () => this.ngZone.run(() => {
                    console.error("GoogleIdentityService: could not load " + GoogleIdentityService.SCRIPT_SRC);
                    script.remove();
                    this.m_loadStarted = false;
                    this.m_ready$.next(false);
                });
                document.head.appendChild(script);
            }
        }
        return this.m_ready$.asObservable();
    }

    /** renders the official Google button into the element (GIS replaces the element content) */
    renderButton(el: HTMLElement, options: IGoogleButtonOptions = {}): void {
        if (!this.m_initialized || !el) return;
        google.accounts.id.renderButton(el, {
            type: "standard",
            size: "large",
            text: "continue_with",
            theme: "filled_blue",
            shape: "rectangular",
            logo_alignment: "left",
            ...options
        });
    }

    /**
     * one click sign in: asks the browser for the Google account chooser (FedCM in Chrome, One Tap elsewhere).
     * Best effort by design: when the browser cannot show it (no Google session, user opted out, cool down after
     * a dismiss) nothing happens and the rendered button remains the way in.
     */
    prompt(): void {
        if (!this.m_initialized) return;
        try {
            // no status callback on purpose: the moment / reason methods are deprecated under FedCM and GIS warns
            // about them, and no UI decision hangs on the outcome because the rendered button is always there
            google.accounts.id.prompt();
        } catch (e) {
            console.debug("google chooser unavailable " + e);
        }
    }

    /** closes an open chooser, used when the user picks another login mode */
    cancelPrompt(): void {
        if (!this.m_initialized) return;
        try {
            google.accounts.id.cancel();
        } catch (e) {
        }
    }

    /** after logout: stop Google from auto selecting the last account on the next visit */
    signOut(): void {
        if (!this.m_initialized) return;
        try {
            google.accounts.id.disableAutoSelect();
        } catch (e) {
        }
    }

    private isAvailable(): boolean {
        return typeof google !== "undefined" && !!google?.accounts?.id;
    }

    private initialize(): void {
        if (this.m_initialized) {
            this.m_ready$.next(true);
            return;
        }
        try {
            google.accounts.id.initialize({
                client_id: GOOGLE_LOGIN_CLIENT_ID,
                auto_select: false,
                cancel_on_tap_outside: true,
                itp_support: true,
                use_fedcm_for_prompt: true,
                callback: (response: { credential?: string }) => this.ngZone.run(() => this.onCredential(response))
            });
            this.m_initialized = true;
            this.m_ready$.next(true);
        } catch (e) {
            console.error("GoogleIdentityService: initialize failed", e);
            this.m_loadStarted = false;
            this.m_ready$.next(false);
        }
    }

    private onCredential(response: { credential?: string }): void {
        const idToken = response?.credential;
        if (!idToken) return;
        const payload = this.decodeJwtPayload(idToken);
        if (!payload) return;
        const user = new SocialUser();
        user.provider = "GOOGLE";
        user.idToken = idToken;
        user.id = payload.sub;
        user.email = payload.email;
        user.name = payload.name;
        user.firstName = payload.given_name;
        user.lastName = payload.family_name;
        user.photoUrl = payload.picture;
        this.m_credential$.next(user);
    }

    /** display only: mars re verifies signature, audience and expiry before anything is trusted */
    private decodeJwtPayload(jwt: string): any {
        try {
            const base64 = jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
            const json = decodeURIComponent(atob(base64).split("").map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)).join(""));
            return JSON.parse(json);
        } catch (e) {
            return null;
        }
    }
}
